import { Injectable, signal } from '@angular/core';
import { SheetSource } from 'src/app/services/store-history/store-history-telemetry.service';

/**
 * Who has the History sheet open, and why.
 *
 * The trigger lives in the header and the drawer lives at the top of the layout, so the two cannot
 * talk through inputs. This is the whole conversation between them: one flag, plus where the
 * opening came from so the sheet can attribute its telemetry.
 *
 * `rendered` lags `opened` on the way down: `MatSidenav` slides out over ~400ms, and unmounting the
 * sheet the moment it is asked to close would empty the panel in front of the merchant. The drawer
 * calls `markClosed()` when the animation is done.
 */
@Injectable({ providedIn: 'root' })
export class HistorySheetService {
  private readonly _opened = signal(false);
  private readonly _rendered = signal(false);
  private readonly _source = signal<SheetSource>('header');

  readonly opened = this._opened.asReadonly();
  readonly rendered = this._rendered.asReadonly();
  readonly source = this._source.asReadonly();

  open(source: SheetSource = 'header'): void {
    this._source.set(source);
    this._rendered.set(true);
    this._opened.set(true);
  }

  close(): void {
    this._opened.set(false);
  }

  toggle(source: SheetSource = 'header'): void {
    if (this._opened()) return this.close();

    this.open(source);
  }

  /** The drawer's exit animation has finished; the sheet can leave the DOM now. */
  markClosed(): void {
    this._rendered.set(false);
  }
}
